import { Link, useParams, Navigate } from "react-router-dom";
import { CheckCircle2, MapPin, ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { FaqAccordion } from "../components/FaqAccordion";
import { CtaBand } from "../components/CtaBand";
import { SERVICES } from "../data/services";
import { CITIES } from "../data/cities";

export default function ServiceDetail() {
    const { slug } = useParams();
    const service = SERVICES.find((s) => s.slug === slug);
    if (!service) return <Navigate to="/services" replace />;
    const idx = SERVICES.indexOf(service);
    const related = [SERVICES[(idx + 1) % SERVICES.length], SERVICES[(idx + 5) % SERVICES.length], SERVICES[(idx + 9) % SERVICES.length]];

    return (
        <div data-testid={`service-page-${service.slug}`}>
            <Seo
                title={`${service.name} Scotland | SIA Licensed ${service.name} Services | Xtreme Protection`}
                description={`${service.short} Xtreme Protection delivers ${service.name.toLowerCase()} across Glasgow, Edinburgh, Aberdeen and 22+ Scottish towns and cities — 24/7, vetted and verified.`}
            />
            <PageHero
                image={service.image}
                imageAlt={`${service.name} services in Scotland`}
                kicker={`Scotland Security Operations // Service ${String(idx + 1).padStart(2, "0")}`}
                title={service.name}
                titleAccent="Across Scotland"
                sub={service.short}
                testidPrefix={`service-${service.slug}`}
            />

            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 grid lg:grid-cols-3 gap-14">
                <div className="lg:col-span-2">
                    <Reveal>
                        <p className="font-mono-xp text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-4">The Service</p>
                        <h2 className="font-display uppercase font-extrabold text-3xl sm:text-4xl tracking-wide text-white leading-none">
                            {service.name} in <span className="text-[#E53E3E]">Scotland</span>
                        </h2>
                        <p className="text-gray-300 text-sm sm:text-base leading-relaxed mt-6">{service.p1}</p>
                        <p className="text-gray-400 text-sm sm:text-base leading-relaxed mt-4">{service.p2}</p>
                    </Reveal>
                    <Reveal delay={0.1} className="mt-12">
                        <h2 className="font-display uppercase font-bold text-2xl tracking-wide text-white mb-6">What you get</h2>
                        <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4" data-testid={`service-deliverables-${service.slug}`}>
                            {service.deliverables.map((d) => (
                                <li key={d} className="flex items-start gap-3 text-sm text-gray-300">
                                    <CheckCircle2 size={16} className="text-[#E53E3E] mt-0.5 shrink-0" /> {d}
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                    <Reveal delay={0.15} className="mt-12">
                        <h2 className="font-display uppercase font-bold text-2xl tracking-wide text-white mb-5">
                            {service.name} questions, answered
                        </h2>
                        <FaqAccordion faqs={service.faqs} testidPrefix={`service-faq-${service.slug}`} />
                    </Reveal>
                </div>
                <aside>
                    <Reveal delay={0.1}>
                        <div className="bg-[#0F1117] border border-white/10 p-6" data-testid={`service-keywords-${service.slug}`}>
                            <h3 className="font-display uppercase tracking-wider text-white text-lg mb-4">Search Coverage</h3>
                            <div className="flex flex-wrap gap-2">
                                {service.keywords.map((k) => (
                                    <span key={k} className="font-mono-xp text-[10px] uppercase tracking-wider text-gray-400 border border-white/10 px-2.5 py-1.5">
                                        {k}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.18} className="mt-6">
                        <div className="bg-[#0F1117] border border-white/10 p-6">
                            <h3 className="font-display uppercase tracking-wider text-white text-lg mb-4">Related Services</h3>
                            <ul className="space-y-3">
                                {related.map((r) => (
                                    <li key={r.slug}>
                                        <Link
                                            to={`/services/${r.slug}`}
                                            data-testid={`service-related-${r.slug}`}
                                            className="group flex items-center justify-between text-sm text-gray-400 hover:text-[#E53E3E] transition-colors"
                                        >
                                            {r.name} Scotland
                                            <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                </aside>
            </section>

            <section className="bg-[#0F1117] border-y border-white/10 py-20" data-testid={`service-locations-${service.slug}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <p className="font-mono-xp text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-4">Coverage</p>
                        <h2 className="font-display uppercase font-extrabold text-3xl sm:text-4xl tracking-wide text-white leading-none">
                            Deploy {service.name.toLowerCase()} in <span className="text-[#E53E3E]">your city</span>
                        </h2>
                        <p className="text-gray-400 text-sm leading-relaxed mt-4 max-w-2xl">
                            {service.name} is available in every town and city we serve. Select your location for local coverage detail.
                        </p>
                    </Reveal>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mt-10">
                        {CITIES.map((c) => (
                            <Link
                                key={c.slug}
                                to={`/locations/${c.slug}`}
                                data-testid={`service-city-link-${service.slug}-${c.slug}`}
                                className="group flex items-center gap-2.5 bg-[#0F1117] hover:bg-[#161922] transition-colors duration-300 px-4 py-3.5"
                            >
                                <MapPin size={13} className="text-[#E53E3E] shrink-0" />
                                <span className="text-sm text-gray-300 group-hover:text-white transition-colors">{service.name} {c.name}</span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <CtaBand title={`Book ${service.name.toLowerCase()} for your Scottish site`} />
        </div>
    );
}
